/**
 * Reading a product photo's ROLE and COLOUR out of the content feed.
 *
 * Content has no media_role column (see context/plan/2026-07-08-catalog-color-images.md), so which
 * image is the packshot — the product-only / die-cut shot rather than the model photo — is a
 * BrandOS reading of the data. These four functions ARE that reading.
 *
 * They were module-local to src/index.ts, where /store/catalog and /store/variant-media use them.
 * The product mirror (helpers/product-mirror.ts) has to answer exactly the same question about
 * exactly the same rows, and two copies of this ladder would drift — a mirrored packshot differing
 * from the live one is the kind of bug nobody looks for. Same functions, one definition.
 */
/** A Shopify CDN image URL resized to `width` (their ?width= transform) so the browser fetches
 *  a small, CDN-cached thumbnail straight from Shopify — far faster than proxying bytes through
 *  brandOS. Non-Shopify / empty URLs pass through unchanged. */
export function shopifyImg(url, width) {
    const u = String(url ?? "");
    if (!u)
        return "";
    if (/cdn\.shopify\.com/i.test(u) && !/[?&]width=/.test(u))
        return u + (u.includes("?") ? "&" : "?") + "width=" + width;
    return u;
}
/** Normalize a colour string for join purposes — collapse hyphens/underscores/runs of whitespace
 *  to a single space so slug-style ("red-wine-white") and spaced ("Red Wine White") forms of the
 *  same colour compare equal downstream (mini-app/staff colourKey() do the same collapse on the
 *  variant side). See context/plan/issues/issue-8-varience-pac.md. */
export function normColour(s) {
    return String(s ?? "").trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}
/**
 * A variant's COLOURWAY and SIZE.
 *
 * SIZE comes from `variant_dimensions` first — it is the structured answer and nothing here has
 * ever found it abridged. COLOUR does not, and that is the whole point of this function.
 *
 * `variant_dimensions.colour` on this estate is the BASE COLOUR, not the colourway: 1,674 of the
 * 3,499 variants carry a dimension that is a strict word-PREFIX of the colour the merchandiser
 * actually named ("Black" against "Black - Ivory Striped"), 1,781 agree, and 4 disagree — in all
 * four of which the specification name is the fuller, better one ("Black - Ivory Striped" against
 * a dimension reading "Black Striped"). On 14 materials the abridgement COLLIDES: two different
 * colourways of one product read out identically.
 *
 * Pacific Knitted Top is the reported one. KAA0202BK-O (Black) and KAA0202BKIV-O (Black - Ivory
 * Striped) both answered "Black", and all three of these followed from that one string:
 *
 *   · POS Inventory printed two rows a cashier could not tell apart, both captioned BLACK.
 *   · The packshot join handed the striped SKU the PLAIN BLACK flat-lay and reported
 *     `packshotExact: true` — a wrong picture that claims to be certain. Two more SKUs of the
 *     same product (Ivory - Black Striped, Ivory - Red Striped) both fell back to a third
 *     colour's photo. Three of six SKUs showed a garment that was not theirs.
 *   · The mini app builds its swatch row from distinct colour strings, so the product offered
 *     FOUR swatches for six colourways and the striped black could not be bought at all.
 *
 * So the colourway is read from `specification_name` whenever the merchandiser wrote one, and the
 * dimension is what is left when they did not. That also subsumes what this function was first
 * added for: the 40 variants that carry no dimensions at all (The Maison Bag ×5, the Mini Maison
 * Bag, Earth Cargo Jeans, Ace Track Pants…), whose colourway has only ever lived in this string.
 *
 * This is NOT re-assembling specName from parts (which /store/product's own note forbids, and for
 * good reason — the separator and bracketing are the operator's decision). It is taking their text
 * apart at the separator they always write.
 *
 * The LAST parenthetical is the one read: "Daily Adore Tote Bag (Free Gift) (Red - Black - White
 * Logo Printed;34*37 CM)" names the offer in the first and the colourway in the last. And it is
 * read only when it carries the `;` — i.e. when it really is the "colour;size" pair (3,459 of
 * 3,499 are; the other 40 have no parenthetical at all). Without that test a variant whose name
 * ends "… (One Size)" would have its SIZE adopted as its colour.
 */
export function variantFacets(v) {
    const dims = Array.isArray(v?.variant_dimensions ?? v?.variantDimensions) ? (v.variant_dimensions ?? v.variantDimensions) : [];
    const dim = (code) => {
        const d = dims.find((x) => code.test(String(x?.dimension_code ?? x?.dimensionCode ?? "").trim()));
        return String(d?.value ?? "").trim();
    };
    const dimColour = dim(/^colou?r$/i);
    const dimSize = dim(/^size$/i);
    const spec = String(v?.specification_name ?? v?.specificationName ?? "");
    const m = spec.match(/\(([^()]*)\)\s*$/);
    const [specColour = "", specSize = ""] = m && m[1].includes(";") ? m[1].split(";") : [];
    // The merchandiser's colourway WINS; the dimension is the fallback, not the other way round.
    // Size keeps its old precedence.
    return { colour: specColour.trim() || dimColour, size: dimSize || specSize.trim() };
}
/**
 * Which of a product's packshots belongs to `colour` — `{url:""}` when none can be said to.
 *
 * `colour` is a COLOURWAY (variantFacets, above) and a media row's tag is written by whoever
 * uploaded the photo, so the two spell the same garment at different lengths: the variant is
 * "Black - Ivory Striped", the flat-lay is tagged "Black-Ivory", and the product also has a
 * plain "Black". The old rule compared them as word SETS, which cannot tell those apart —
 * "black ivory striped" contains "black", so the striped SKU matched the plain black photo, and
 * "ivory black striped" also contains both words, so a set rule has no way to prefer either.
 *
 * They are compared as ORDERED word prefixes instead, which is how a colourway is actually
 * written: the base colour first, the trim after it. The ladder, in order:
 *
 *   1. the tag that IS the colourway;
 *   2. the LONGEST tag that is a prefix of it ("black ivory" for "black ivory striped", beating
 *      the plain "black" that is also a prefix) — a tie at that length is a genuine ambiguity
 *      and stops here;
 *   3. going the other way, the one tag the colourway is a prefix OF, when exactly one is — this
 *      is a variant named more coarsely than its photo, and more than one candidate is again a
 *      refusal.
 *
 * There is deliberately NO unordered fallback and NO "the product has only one packshot, use
 * it" rule for a product with several colourways: both existed, and between them they were the
 * whole of the estate's 27 confidently-wrong pictures. Measured over the 1,206 active variants,
 * this ladder gives 772 of them their own colourway's flat-lay against the old rule's 756, and
 * hands out none that the tag cannot be read as naming. `colourways` is how many distinct
 * colourways the PRODUCT has; at 1 the single-packshot fallback is unambiguous and kept.
 *
 * The storefront runs this same ladder per cart line — pickPackshot in apps/api/media.ts calls
 * straight into it, rather than restating it, because the two drifting is exactly how a line's
 * picture and its product page's picture come to disagree.
 */
// `a` is a word-prefix of `b` — "black ivory" of "black ivory striped", not of
// "ivory black striped". Compared with the spaces squashed out, but only ending on one of
// `b`'s word boundaries: the photographer's "DARKBROWN_01_L" is a prefix of "Dark Brown -
// Light Blue Plaid", while "dark" is still not a prefix of "darkbrown".
function prefixOf(a, b) {
    const s = a.join("");
    let acc = "";
    for (const w of b) {
        acc += w;
        if (acc === s)
            return true;
        if (acc.length >= s.length)
            return false;
    }
    return false;
}
/**
 * Which of the PRODUCT's colourways a photo's label names — normalised, or "" when it names none
 * or more than one.
 *
 * A photo's label is its `colour_tag`, else what its alt text says ("Pink_01" → "pink"). The alt
 * is Shopify's word for the colour, and on a product with two pink colourways "pink" is BOTH of
 * them: Sana Knitted Top's untagged model shots read "Pink", matchPackshot took "pink" as a
 * prefix of "Pink - Black Details", and that SKU — which has no photo at all — showed a model in
 * Pink - Dark Brown Striped (reported 2026-09-25). A label is only a colour when it can mean ONE
 * colourway; otherwise the photo is Shared, which every screen reads as "no photo of this
 * colour" rather than as a wrong one.
 *
 * `ways` is each variant's colourway plus its base-colour dimension, because the dimension is
 * often the word the photographer used ("Black-Striped" for "Black - Ivory Striped").
 */
/** A media row's colour word out of its alt text: "Navy_01_L" / "Navy_01" / "Navy_L" → "Navy". */
export function altLabel(alt) {
    return String(alt ?? "").trim().replace(/_\d+_l$/i, "").replace(/_l$/i, "").replace(/_\d+$/, "").trim();
}
/** The `ways` ownColourway() takes, from a product's variant rows. */
export function waysOf(variants) {
    return (Array.isArray(variants) ? variants : []).map((v) => {
        const dims = Array.isArray(v?.variant_dimensions ?? v?.variantDimensions) ? (v.variant_dimensions ?? v.variantDimensions) : [];
        const d = dims.find((x) => /^colou?r$/i.test(String(x?.dimension_code ?? x?.dimensionCode ?? "").trim()));
        return { colour: variantFacets(v).colour, base: String(d?.value ?? "").trim() };
    });
}
/**
 * The colourway a media row is a photo OF: its `colour_tag`, resolved by ownColourway() — and
 * NOTHING else. The Product Media card groups photos by that tag, and it is the one place a person
 * says which colour a photo is; an untagged photo sits in Shared there. Reading the alt text too
 * (Shopify's "Navy_01") filed Sana's untagged model shot under Navy Blue - Ivory Striped in the mini
 * app while the Media card showed that colour with its flat-lay only (reported 2026-09-25). `alt`
 * is accepted and ignored so callers keep one signature.
 */
export function photoColourway(tag, _alt, ways) {
    return ownColourway(String(tag ?? "").trim(), ways);
}
export function ownColourway(label, ways) {
    const want = normColour(label);
    if (!want)
        return "";
    const squash = (s) => s.replace(/ /g, "");
    const lw = want.split(" ").filter(Boolean);
    // In order of certainty: the label IS a colourway; the label is ONE colourway's base-colour
    // dimension (Shopify's own word — "Black" is Black - Ivory Details on Sana, "Black-Striped"
    // the striped one); the label is a word-prefix of ONE colourway. Each rung only answers when
    // it names exactly one colourway.
    const byExact = new Set(), byBase = new Set(), byPrefix = new Set();
    for (const w of ways) {
        const cw = normColour(w.colour);
        if (!cw)
            continue;
        if (squash(cw) === squash(want))
            byExact.add(cw);
        if (w.base && squash(normColour(w.base)) === squash(want))
            byBase.add(cw);
        const cww = cw.split(" ").filter(Boolean);
        if (prefixOf(lw, cww) || prefixOf(cww, lw))
            byPrefix.add(cw);
    }
    for (const rung of [byExact, byBase, byPrefix]) {
        if (rung.size === 1)
            return [...rung][0];
        if (rung.size > 1)
            return "";
    }
    return "";
}
export function matchPackshot(packs, colour, opts) {
    const want = normColour(colour);
    if (want) {
        const words = want.split(" ").filter(Boolean);
        const squash = (s) => s.replace(/ /g, "");
        const exact = packs.find((p) => squash(p.colour) === squash(want));
        if (exact)
            return { url: exact.url };
        let best = null;
        let tied = false;
        for (const p of packs) {
            const pw = p.colour.split(" ").filter(Boolean);
            if (!prefixOf(pw, words))
                continue;
            const len = pw.join("").length;
            if (!best || len > best.len) {
                best = { url: p.url, len };
                tied = false;
            }
            else if (len === best.len && p.url !== best.url)
                tied = true;
        }
        if (best)
            return { url: tied ? "" : best.url };
        const finer = packs.filter((p) => prefixOf(words, p.colour.split(" ").filter(Boolean)));
        if (finer.length === 1)
            return { url: finer[0].url };
        if (finer.length > 1)
            return { url: "" };
        // A colourway that matched nothing does NOT fall through to the product's lone packshot
        // when the product has other colourways — that is how a striped SKU came to show the
        // plain one's photo.
        if ((opts?.colourways ?? 1) > 1)
            return { url: "" };
    }
    return { url: packs.length === 1 ? packs[0].url : "" };
}
/**
 * Is this media row the PACKSHOT rather than a model/lifestyle photo?
 *
 * The estate says it in TWO ways, not one:
 *
 *   alt text  "Navy_01_L"            → 691 rows
 *   file name ORIGINAL_DI_HAEDBAND   → 37 more rows whose alt is junk ("m", "Hover", "")
 *
 * Reading only the alt left 18 products whose ONLY flat-lay is one of those 37 showing a model
 * shot as their packshot everywhere — swatch thumbnails, cart lines, POS cards. Checked against
 * all 3,174 media rows in this tenant: every row the file-name rule adds is a genuine die-cut
 * (…_DICUT_…, …_DI_…, Packshot11_1322…, NYLONPOUCH_PACKSHOT_BLACK), none is a model shot.
 */
export function isPackshotRow(alt, sourceUrl) {
    if (/_l$/i.test(String(alt ?? "").trim()))
        return true;
    const file = String(sourceUrl ?? "").split("/").pop() ?? "";
    // `di` only as its own name token, so a "DIGITAL"/"DIAMOND" file cannot be caught by it.
    return /dicut|die[-_ ]?cut|pack[-_ ]?shot|(^|[-_ ])di([-_ ]|$)/i.test(file);
}
/**
 * What a storefront shows of a material's photos, in the order it shows them — from the rows
 * `content/merchandising/media/list` answers with.
 *
 * The projection `storefront-feed/media` returns is the natural read for this and it is the one
 * that cannot be used, for one missing column: it answers material_no / source_url / colour_tag /
 * alt_en / sort_order / has_base64 and NO row identity. Every image URL built from it therefore
 * addresses a photo by its POSITION (`/media/img?material_no=X&i=2`), and a positional URL is a
 * lie the moment somebody reorders the gallery — the URL is unchanged, the picture behind it is
 * not, and every browser holding the old bytes goes on showing them until its copy expires.
 * media/list carries `asset_ref`, costs the same one Hub read, and does not inline the bytes
 * (`hasBase64` stands in for them), so the row is ~1 KB.
 *
 * The filters below are `ProductMediaAdapter.listStorefront`'s, restated because this read is the
 * AUTHORING one: it does not apply them itself. A row is shown when it is a `product_image`, is
 * not archived, and actually has a picture — a URL to fetch or bytes in the row. Order is
 * `sort_order`, then `id`, which is what the storefront feed and `/media/img?i=` both use, so a
 * gallery built here and a photo fetched positionally agree about which one is third.
 */
export function storefrontMediaRows(items) {
    const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
    return (Array.isArray(items) ? items : [])
        .filter((r) => String(r?.mediaType ?? r?.media_type ?? "product_image") === "product_image")
        .filter((r) => String(r?.status ?? "") !== "archived")
        .filter((r) => !!String(r?.sourceUrl ?? r?.source_url ?? "") || r?.hasBase64 === true || r?.has_base64 === true
        || !!String(r?.imageBase64 ?? r?.image_base64 ?? ""))
        .map((r) => ({
        assetRef: String(r?.assetRef ?? r?.asset_ref ?? "").trim(),
        sourceUrl: String(r?.sourceUrl ?? r?.source_url ?? ""),
        colourTag: String(r?.colourTag ?? r?.colour_tag ?? ""),
        // `alt_en` on the feed, an {en,th} object on the authoring read — the packshot heuristic
        // wants the English string either way.
        alt: String(r?.alt_en ?? r?.altEn ?? (r?.altText ?? r?.alt_text ?? {})?.en ?? r?.alt ?? ""),
        sortOrder: num(r?.sortOrder ?? r?.sort_order),
        id: num(r?.id),
    }))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
        .map(({ id, ...row }) => row);
}
/**
 * The same-origin URL for one product photo.
 *
 * By `asset_ref` whenever the row has one, because that names the ROW: the bytes behind it never
 * change, so the browser may keep them for a year, and a reorder cannot make the URL point at a
 * different picture. The positional form is the fallback for a caller that has no ref (the
 * catalogue grid, whose feed does not carry one) and is exactly as stale-prone as it sounds.
 */
export function productImageUrl(assetRef, materialNo, index) {
    return assetRef
        ? "/media/img?asset_ref=" + encodeURIComponent(assetRef)
        : "/media/img?material_no=" + encodeURIComponent(materialNo) + "&i=" + index;
}
/** The colour a packshot's alt text carries: "Light Grey_01_L" → "light grey", "IVORY_01_L" → "ivory". */
export function colourFromAlt(alt) {
    return String(alt ?? "").replace(/_\d+_l$/i, "").replace(/_l$/i, "").trim().toLowerCase();
}
//# sourceMappingURL=product-media.js.map